import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import { API } from "@/config/api";

const OffMarket = () => {
  const { t, language } = useLanguage();
  const prefersReducedMotion = useReducedMotion();
  const enter = prefersReducedMotion
    ? { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0 } }
    : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } };
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    budget_min: "",
    budget_max: "",
    preferred_locations: [],
    property_types: [],
    timeline: "",
    additional_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const locations = [
    "Cologny", "Vandoeuvres", "Collonge-Bellerive", "Genthod", 
    "Bellevue", "Champel", "Eaux-Vives", "Verbier", "Gstaad"
  ];

  const propertyTypes = ["Villa", "Apartment", "Penthouse", "Chalet", "Townhouse"];

  const timelines = [
    { value: "immediately", label: language === 'fr' ? "Immédiatement" : "Immediately" },
    { value: "3months", label: language === 'fr' ? "Sous 3 mois" : "Within 3 months" },
    { value: "6months", label: language === 'fr' ? "Sous 6 mois" : "Within 6 months" },
    { value: "12months", label: language === 'fr' ? "Sous 12 mois" : "Within 12 months" },
    { value: "flexible", label: language === 'fr' ? "Flexible" : "No specific timeline" },
  ];

  const budgetRanges = [
    { value: "1000000", label: "CHF 1M" },
    { value: "3000000", label: "CHF 3M" },
    { value: "5000000", label: "CHF 5M" },
    { value: "10000000", label: "CHF 10M" },
    { value: "20000000", label: "CHF 20M" },
    { value: "50000000", label: "CHF 50M+" },
  ];

  const toggleSelection = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.preferred_locations.length === 0) {
      toast.error(language === 'fr' ? "Veuillez sélectionner au moins une localisation" : "Please select at least one location");
      return;
    }
    if (formData.property_types.length === 0) {
      toast.error(language === 'fr' ? "Veuillez sélectionner au moins un type de bien" : "Please select at least one property type");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        budget_min: parseInt(formData.budget_min) || 1000000,
        budget_max: parseInt(formData.budget_max) || 50000000,
      };
      
      const response = await axios.post(`${API}/off-market/apply`, payload);
      toast.success(response.data.message);
      setSubmitted(true);
    } catch (error) {
      toast.error(language === 'fr' ? "Échec de l'envoi" : "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-byron-bg" data-testid="off-market-page">
        <div className="header-spacer" />
        <section className="py-10 md:py-14">
          <div className="container-byron">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-lg mx-auto text-center"
            >
              <div className="w-16 h-16 border border-[#8B7355] rounded-full flex items-center justify-center mb-8 mx-auto">
                <Check size={32} className="text-[#8B7355]" />
              </div>
              <h1 className="heading-primary mb-6">
                {language === 'fr' ? 'Demande reçue' : 'Application Received'}
              </h1>
              <p className="body-text">
                {language === 'fr' 
                  ? 'Nous avons bien reçu votre demande d\'accès. Notre équipe vous contactera dans les plus brefs délais.'
                  : 'We have received your access request. Our team will contact you shortly.'}
              </p>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-byron-bg" data-testid="off-market-page">
      <SEO 
        title="Off Market"
        description="Accédez à notre collection privée de propriétés off-market à Genève. Des biens exclusifs jamais publiés, réservés à une clientèle sélectionnée."
        canonical="/off-market"
      />
      {/* Header spacer */}
      <div className="header-spacer" />

      {/* Page Content */}
      <section className="py-10 md:py-14">
        <div className="container-byron">
          <div className="max-w-3xl mx-auto">
            
            {/* Introduction */}
            <motion.div
              {...enter}
              className="text-center mb-6 md:mb-8"
            >
              <p className="label-text mb-4">
                {language === 'fr' ? 'Accès privilégié' : 'Private Access'}
              </p>
              <h1 className="heading-primary mb-6">
                {language === 'fr' ? 'Off Market' : 'Off Market'}
              </h1>
              <p className="body-text max-w-xl mx-auto">
                {language === 'fr' 
                  ? 'Accédez à des opportunités exclusives, confidentielles et non diffusées publiquement. Complétez ce formulaire pour rejoindre notre cercle privilégié.'
                  : 'Access exclusive, confidential opportunities not publicly listed. Complete this form to join our privileged circle.'}
              </p>
            </motion.div>

            {/* Form */}
            <motion.div
              {...enter}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }
              }
            >
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-[#E8E6E1] p-8 md:p-12 space-y-10"
                data-testid="off-market-form"
              >
                {/* Personal Details */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#888] mb-6 pb-3 border-b border-[#E5E5E0]">
                    {language === 'fr' ? 'Vos coordonnées' : 'Personal Details'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.1em] text-[#888] mb-2">
                        {language === 'fr' ? 'Prénom' : 'First Name'}
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        className="w-full bg-transparent border-b border-[#E5E5E0] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                        data-testid="off-market-first-name"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.1em] text-[#888] mb-2">
                        {language === 'fr' ? 'Nom' : 'Last Name'}
                      </label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        className="w-full bg-transparent border-b border-[#E5E5E0] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                        data-testid="off-market-last-name"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.1em] text-[#888] mb-2">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full bg-transparent border-b border-[#E5E5E0] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                        data-testid="off-market-email"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.1em] text-[#888] mb-2">
                        {language === 'fr' ? 'Téléphone' : 'Phone'}
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="w-full bg-transparent border-b border-[#E5E5E0] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                        data-testid="off-market-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Budget */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#888] mb-6 pb-3 border-b border-[#E5E5E0]">
                    {language === 'fr' ? 'Budget' : 'Budget Range'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.1em] text-[#888] mb-2">
                        {language === 'fr' ? 'Minimum' : 'Minimum'}
                      </label>
                      <select
                        value={formData.budget_min}
                        onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                        required
                        className="w-full bg-transparent border-b border-[#E5E5E0] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                        data-testid="off-market-budget-min"
                      >
                        <option value="">{language === 'fr' ? 'Sélectionner' : 'Select'}</option>
                        {budgetRanges.map((range) => (
                          <option key={`min-${range.value}`} value={range.value}>{range.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.1em] text-[#888] mb-2">
                        {language === 'fr' ? 'Maximum' : 'Maximum'}
                      </label>
                      <select
                        value={formData.budget_max}
                        onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                        required
                        className="w-full bg-transparent border-b border-[#E5E5E0] py-3 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none"
                        data-testid="off-market-budget-max"
                      >
                        <option value="">{language === 'fr' ? 'Sélectionner' : 'Select'}</option>
                        {budgetRanges.map((range) => (
                          <option key={`max-${range.value}`} value={range.value}>{range.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Locations */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#888] mb-6 pb-3 border-b border-[#E5E5E0]">
                    {language === 'fr' ? 'Localisations souhaitées' : 'Preferred Locations'}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {locations.map((location) => (
                      <button
                        key={location}
                        type="button"
                        onClick={() => toggleSelection("preferred_locations", location)}
                        className={`px-4 py-2.5 text-sm transition-all border ${
                          formData.preferred_locations.includes(location)
                            ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                            : "bg-transparent text-[#1A1A1A] border-[#E5E5E0] hover:border-[#1A1A1A]"
                        }`}
                        data-testid={`location-${location.toLowerCase()}`}
                      >
                        {location}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Property Types */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#888] mb-6 pb-3 border-b border-[#E5E5E0]">
                    {language === 'fr' ? 'Type de bien' : 'Property Type'}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {propertyTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleSelection("property_types", type)}
                        className={`px-4 py-2.5 text-sm transition-all border ${
                          formData.property_types.includes(type)
                            ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                            : "bg-transparent text-[#1A1A1A] border-[#E5E5E0] hover:border-[#1A1A1A]"
                        }`}
                        data-testid={`type-${type.toLowerCase()}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#888] mb-6 pb-3 border-b border-[#E5E5E0]">
                    {language === 'fr' ? 'Délai' : 'Timeline'}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {timelines.map((timeline) => (
                      <button
                        key={timeline.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, timeline: timeline.value })}
                        className={`px-4 py-2.5 text-sm transition-all border ${
                          formData.timeline === timeline.value
                            ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                            : "bg-transparent text-[#1A1A1A] border-[#E5E5E0] hover:border-[#1A1A1A]"
                        }`}
                        data-testid={`timeline-${timeline.value}`}
                      >
                        {timeline.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.15em] text-[#888] mb-6 pb-3 border-b border-[#E5E5E0]">
                    {language === 'fr' ? 'Informations complémentaires' : 'Additional Information'}
                  </h3>
                  <textarea
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    rows={4}
                    placeholder={language === 'fr' ? "Vos critères spécifiques..." : "Any specific requirements..."}
                    className="w-full bg-[#FAF9F6] border border-[#E5E5E0] p-4 text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none placeholder:text-[#999] resize-none"
                    data-testid="off-market-notes"
                  />
                </div>

                {/* Submit */}
                <div className="text-center pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-3 bg-[#1A1A1A] text-white py-4 px-10 text-xs uppercase tracking-[0.15em] hover:bg-[#333] transition-colors"
                    data-testid="off-market-submit"
                  >
                    {submitting 
                      ? (language === 'fr' ? "Envoi en cours..." : "Submitting...") 
                      : (language === 'fr' ? "Envoyer ma demande" : "Submit Application")}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            </motion.div>

          </div>
        </div>
      </section>
    </main>
  );
};

export default OffMarket;
